import { Button } from '@/app/components/button';
import { Image } from '@/app/components/media/image';
import { Link } from '@/app/components/navigation/link';
import type { ServiceCenterOverviewItemProps } from '@/app/components/sections/service/service-center/overview/types';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { createDataGA } from '@/app/utils/createDataGA';

export const ServiceCenterOverviewCard = ({
  image,
  title,
  desc,
  cta,
  testId = 'overview-card',
  eventName = 'overview-card',
  className,
  isHoverable = false,
}: ServiceCenterOverviewItemProps) => {
  if (!title) return null;

  const wrapperClassName = cn(
    'bg-silver-50 flex size-full flex-col overflow-hidden rounded-sm shadow-sm md:rounded-md',
    className
  );
  const wrapperProps = !!isHoverable
    ? {
        ...createDataAttributes({ test: `${testId}-wrapper` }),
        ...createDataGA({
          click: true,
          eventName: eventName,
          text: title,
          url: cta?.href,
        }),
      }
    : { ...createDataAttributes({ test: `${testId}-wrapper` }) };
  const buttonProps = !!isHoverable ? { ...cta, href: undefined } : { ...cta };
  const content = () => {
    return (
      <>
        <Image
          src={image.src}
          alt={image.alt}
          width={image.width}
          height={image.height}
          layout="ratio"
          aspectRatio="aspect-3-2"
          srcSetOptions={{ crop: '3:2' }}
          sizes="384px"
          className="bg-silver-900"
          hoverEffect={isHoverable ? 'zoom' : undefined}
          isGroupHover={isHoverable}
          attributes={createDataAttributes({ test: `${testId}-image` })}
        />

        <div
          className="where-span:text-inline-wrap flex flex-auto flex-col gap-2 p-6"
          {...createDataAttributes({ test: `${testId}-content` })}
        >
          <h3
            className="text-sub-h2 font-noto-bold line-clamp-2 text-gray-900"
            dangerouslySetInnerHTML={{ __html: title }}
            {...createDataAttributes({ test: `${testId}-title` })}
          />
          {!!desc &&
            (Array.isArray(desc) ? (
              <ul className="list-ul where-span:text-inline-wrap text-body-1 text-gray-600">
                {desc.map((item, index) => (
                  <li key={index} dangerouslySetInnerHTML={{ __html: item }} />
                ))}
              </ul>
            ) : (
              <p
                className="text-body-1 text-gray-600"
                dangerouslySetInnerHTML={{ __html: desc }}
                {...createDataAttributes({ test: `${testId}-description` })}
              />
            ))}
          {!!cta && (
            <div className="flex flex-auto items-end justify-center">
              <Button
                as="div"
                {...buttonProps}
                color="black"
                className="group-hover:text-red-500 group-focus-visible:text-red-500 group-active:text-red-500"
                {...createDataGA({
                  click: true,
                  eventName: eventName,
                  text: title,
                  url: cta.href,
                })}
              />
            </div>
          )}
        </div>
      </>
    );
  };

  return (
    <>
      {!!isHoverable ? (
        <Link
          href={`${cta?.href}`}
          className={wrapperClassName}
          {...wrapperProps}
        >
          {content()}
        </Link>
      ) : (
        <article className={wrapperClassName} {...wrapperProps}>
          {content()}
        </article>
      )}
    </>
  );
};

ServiceCenterOverviewCard.displayName = 'ServiceCenterOverviewCard';
